import { ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material';
import { EMPTY, forkJoin, Observable, of, Subscription } from 'rxjs';
import { catchError, expand, map, reduce, tap } from 'rxjs/operators';
import { AppService } from 'src/@core/services/app.service';
import { JobService } from 'src/@core/services/job/job.service';
import { NotificationService } from 'src/@core/services/notification.service';
import { CspSafeAudioPlayer, getFetchAudioErrorMessage } from 'src/@core/utils/audio-playback.util';
import { getWhatsAppDocumentsErrorMessage, openPresignedDownload } from 'src/@core/utils/presigned-download.util';
import { EnterpriseAdminService } from 'src/app/modules/enterprise-admin/enterprise-admin.service';
import { CallSummaryPopupComponent } from '../connected-leads/call-summary-popup/call-summary-popup.component';
import { EditOutcomePopupComponent } from '../connected-leads/edit-outcome-popup/edit-outcome-popup.component';
import { EditTasksPopupComponent } from '../connected-leads/edit-tasks-popup/edit-tasks-popup.component';
import { AssignedLeadUser } from '../entity-leads/models/entity-leads.types';
import { buildPageNumbers } from '../entity-leads/utils/entity-leads.utils';
import { extractPostCallSignals, resolveLeadTaskPrompt } from '../entity-leads/utils/post-call-signals.util';
import { ClientProfileComponent } from '../view-project-new/client-profile/client-profile.component';

/** Rows per request while loading a user's assigned list, and a hard stop per user. */
const FETCH_PAGE_SIZE = 100;
const FETCH_MAX_PAGES = 50;
const SEARCH_DEBOUNCE_MS = 250;
const UNKNOWN_OUTCOME = 'Unknown';

interface TimeframeOption {
  label: string;
  value: string;
}

interface CountOption {
  key: string;
  label: string;
  count: number;
}

/**
 * Leads handed out through an outcome's assign_users.
 *
 * - `mine` (My Leads): the signed-in user's leads across every project.
 * - `project` (a project's Assigned tab): every lead the project's outcomes
 *   assigned, to whichever user, with a per-user breakdown.
 *
 * The listing API only narrows by one user at a time and its counts and export
 * are project-wide, so the whole assigned list is loaded once and filtered,
 * counted, paged and exported here. That keeps the summary, the table and the
 * export in agreement for every filter.
 */
@Component({
  selector: 'hour4u-admin-assigned-leads',
  templateUrl: './assigned-leads.component.html',
  styleUrls: ['./assigned-leads.component.scss']
})
export class AssignedLeadsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() mode: 'mine' | 'project' = 'mine';
  /** `mine`: whose leads to list. */
  @Input() userId: string | null = null;
  /** `project`: the project whose assigned leads to list. */
  @Input() projectDetails: any = null;

  profile = this.appService.getCurrentAccount();

  allRows: any[] = [];
  isLoading = false;
  loadFailed = false;
  isExporting = false;

  // Filters
  searchQuery = '';
  timeframeOptions: TimeframeOption[] = [
    { label: 'All time', value: 'all' },
    { label: 'Today', value: 'today' },
    { label: 'This week', value: 'week' },
    { label: 'This month', value: 'month' },
    { label: 'Last 30 days', value: 'last30' },
    { label: 'Custom range', value: 'custom' }
  ];
  selectedTimeframe = 'all';
  customStartDate = '';
  customEndDate = '';
  selectedProjectId: string | null = null;
  selectedUserId: string | null = null;
  selectedOutcome: string | null = null;

  // Derived, recomputed by applyFilters() rather than read through getters, so a
  // change-detection pass never re-filters the list.
  filteredRows: any[] = [];
  pageRows: any[] = [];
  outcomeSummary: CountOption[] = [];
  outcomeTotal = 0;
  userSummary: CountOption[] = [];
  userTotal = 0;
  projectOptions: CountOption[] = [];

  // Pagination
  pageSizeOptions = [10, 25, 50, 100];
  pageSize = 10;
  pageIndex = 1;
  totalPages = 0;
  pageNumbers: Array<number | string> = [];

  // Recording playback
  currentAudio: CspSafeAudioPlayer | null = null;
  playingRecordingId: string | null = null;
  private currentRecordingUrl: string | null = null;

  downloadingWaDocsClientNumber: string | null = null;

  private projectCache = new Map<string, any>();
  private loadSub: Subscription | null = null;
  private searchTimeout: any;

  constructor(
    private appService: AppService,
    private enterpriseAdminService: EnterpriseAdminService,
    private jobService: JobService,
    private notify: NotificationService,
    private dialog: MatDialog,
    private cd: ChangeDetectorRef,
  ) { }

  ngOnInit() {
    this.loadRows();
  }

  ngOnChanges(changes: SimpleChanges) {
    const userChanged = changes.userId && !changes.userId.firstChange;
    const projectChanged = changes.projectDetails && !changes.projectDetails.firstChange
      && this.projectIdOf(changes.projectDetails.previousValue) !== this.projectIdOf(changes.projectDetails.currentValue);
    if (userChanged || projectChanged) {
      this.loadRows();
    }
  }

  ngOnDestroy() {
    if (this.loadSub) {
      this.loadSub.unsubscribe();
    }
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    this.resetAudio();
  }

  get isMine(): boolean {
    return this.mode === 'mine';
  }

  /** Users this project's outcomes hand leads to, in outcome order. */
  get projectAssignees(): AssignedLeadUser[] {
    const outcomes = (this.projectDetails && this.projectDetails.expected_outcomes) || [];
    const seen = new Set<string>();
    const users: AssignedLeadUser[] = [];
    outcomes.forEach((outcome: any) => {
      ((outcome && outcome.assign_users) || []).forEach((entry: any) => {
        const userId = String((entry && (entry.user_id || entry.userId)) || '').trim();
        if (userId && !seen.has(userId)) {
          seen.add(userId);
          users.push({ user_id: userId, user_name: String((entry && (entry.user_name || entry.userName)) || '').trim() });
        }
      });
    });
    return users;
  }

  get hasActiveFilters(): boolean {
    return !!(this.searchQuery.trim() || this.selectedTimeframe !== 'all' || this.selectedProjectId
      || this.selectedUserId || this.selectedOutcome);
  }

  get rangeLabel(): string {
    const total = this.filteredRows.length;
    if (!total) {
      return '0 leads';
    }
    const start = (this.pageIndex - 1) * this.pageSize + 1;
    const end = Math.min(this.pageIndex * this.pageSize, total);
    return `${start}–${end} of ${total} lead${total === 1 ? '' : 's'}`;
  }

  // ----- Loading -----

  loadRows() {
    const entityId = this.getEntityId();
    if (this.loadSub) {
      this.loadSub.unsubscribe();
    }
    if (!entityId) {
      return;
    }

    let request: Observable<any[]>;
    if (this.isMine) {
      if (!this.userId) {
        return;
      }
      request = this.fetchAllForUser(entityId, this.userId, undefined);
    } else {
      const projectId = this.projectIdOf(this.projectDetails);
      const assignees = this.projectAssignees;
      if (!projectId || !assignees.length) {
        this.allRows = [];
        this.applyFilters();
        return;
      }
      request = forkJoin(assignees.map(user =>
        this.fetchAllForUser(entityId, user.user_id, projectId).pipe(
          map(rows => rows.map(row => this.withAssignee(row, user)))
        )
      )).pipe(map(lists => lists.reduce((all: any[], rows: any[]) => all.concat(rows), [])));
    }

    this.isLoading = true;
    this.loadFailed = false;
    this.cd.markForCheck();
    this.loadSub = request.subscribe(rows => {
      this.allRows = this.sortByLatestActivity(rows);
      this.isLoading = false;
      this.syncFiltersWithRows();
      this.applyFilters();
    }, (err) => {
      console.error('Failed to load assigned leads:', err);
      this.allRows = [];
      this.isLoading = false;
      this.loadFailed = true;
      this.applyFilters();
    });
  }

  refresh() {
    if (this.isLoading) {
      return;
    }
    // Row actions re-read each project afterwards, in case its outcomes changed.
    this.projectCache.clear();
    this.loadRows();
  }

  private fetchAllForUser(entityId: string, userId: string, projectId: string | undefined): Observable<any[]> {
    const fetchPage = (pageNum: number) => this.enterpriseAdminService.getCallHistoryProject(
      entityId, projectId, FETCH_PAGE_SIZE, pageNum, undefined, { user_id: userId }
    ).pipe(map((res: any) => ({ res, pageNum })));

    return fetchPage(1).pipe(
      expand(({ res, pageNum }) => {
        const data = res && Array.isArray(res.data) ? res.data : [];
        const hasNext = !!(res && res.pagination && res.pagination.has_next_page === true && data.length);
        return hasNext && pageNum < FETCH_MAX_PAGES ? fetchPage(pageNum + 1) : EMPTY;
      }),
      map(({ res }) => (res && Array.isArray(res.data) ? res.data : [])),
      reduce((all: any[], rows: any[]) => all.concat(rows), [])
    );
  }

  private withAssignee(row: any, user: AssignedLeadUser): any {
    if (row && !row.assigned_user_name && user.user_name) {
      row.assigned_user_name = user.user_name;
    }
    if (row && !row.assigned_user_id) {
      row.assigned_user_id = user.user_id;
    }
    return row;
  }

  private sortByLatestActivity(rows: any[]): any[] {
    return (rows || []).slice().sort((a, b) => this.toSeconds(b && b.start_time) - this.toSeconds(a && a.start_time));
  }

  /** Drop a picked project or user that the reloaded list no longer has. */
  private syncFiltersWithRows() {
    if (this.selectedProjectId && !this.allRows.some(row => row.project_id === this.selectedProjectId)) {
      this.selectedProjectId = null;
    }
  }

  // ----- Filtering -----

  onSearchChange(value: string) {
    this.searchQuery = value || '';
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    this.searchTimeout = setTimeout(() => this.onFilterChange(), SEARCH_DEBOUNCE_MS);
  }

  onTimeframeChange() {
    if (this.selectedTimeframe !== 'custom') {
      this.customStartDate = '';
      this.customEndDate = '';
    }
    this.onFilterChange();
  }

  onFilterChange() {
    this.pageIndex = 1;
    this.applyFilters();
  }

  selectOutcome(outcome: string | null) {
    this.selectedOutcome = outcome && outcome !== this.selectedOutcome ? outcome : null;
    this.onFilterChange();
  }

  selectUser(userId: string | null) {
    this.selectedUserId = userId && userId !== this.selectedUserId ? userId : null;
    this.onFilterChange();
  }

  clearFilters() {
    this.searchQuery = '';
    this.selectedTimeframe = 'all';
    this.customStartDate = '';
    this.customEndDate = '';
    this.selectedProjectId = null;
    this.selectedUserId = null;
    this.selectedOutcome = null;
    this.onFilterChange();
  }

  /**
   * Each breakdown is counted with every filter except its own, so picking an
   * outcome still shows how the other outcomes compare, and picking a user still
   * shows everyone's share.
   */
  private applyFilters() {
    const range = this.resolveDateRange();
    const query = this.searchQuery.trim().toLowerCase();

    const common = this.allRows.filter(row => {
      if (query) {
        const name = String(row.client_name || '').toLowerCase();
        const number = String(row.client_number || '').toLowerCase();
        if (!name.includes(query) && !number.includes(query)) {
          return false;
        }
      }
      if (range) {
        const at = this.toSeconds(row.start_time);
        if (!at || at < range.from || at > range.to) {
          return false;
        }
      }
      return true;
    });

    this.projectOptions = this.countBy(common, row => row.project_id, row => row.project_name || 'Untitled project');

    const inProject = this.selectedProjectId
      ? common.filter(row => row.project_id === this.selectedProjectId)
      : common;

    this.userSummary = this.buildUserSummary(inProject);
    this.userTotal = inProject.length;

    const forOutcomes = this.selectedUserId
      ? inProject.filter(row => row.assigned_user_id === this.selectedUserId)
      : inProject;

    this.outcomeSummary = this.countBy(forOutcomes, row => this.outcomeOf(row), row => this.outcomeOf(row))
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
    this.outcomeTotal = forOutcomes.length;
    if (this.selectedOutcome && !this.outcomeSummary.some(o => o.key === this.selectedOutcome)) {
      // The outcome has no leads under the other filters; keep the pick visible
      // at zero rather than silently dropping it.
      this.outcomeSummary.push({ key: this.selectedOutcome, label: this.selectedOutcome, count: 0 });
    }

    this.filteredRows = this.selectedOutcome
      ? forOutcomes.filter(row => this.outcomeOf(row) === this.selectedOutcome)
      : forOutcomes;

    this.updatePage();
  }

  private buildUserSummary(rows: any[]): CountOption[] {
    if (this.isMine) {
      return [];
    }
    const counts = this.countBy(rows, row => row.assigned_user_id, row => row.assigned_user_name || 'Unnamed user');
    // Everyone the project assigns to is listed, including users with no leads yet.
    this.projectAssignees.forEach(user => {
      if (!counts.some(c => c.key === user.user_id)) {
        counts.push({ key: user.user_id, label: user.user_name || 'Unnamed user', count: 0 });
      }
    });
    return counts.sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  }

  private countBy(rows: any[], keyOf: (row: any) => string, labelOf: (row: any) => string): CountOption[] {
    const byKey = new Map<string, CountOption>();
    rows.forEach(row => {
      const key = keyOf(row);
      if (!key) {
        return;
      }
      const entry = byKey.get(key);
      if (entry) {
        entry.count++;
      } else {
        byKey.set(key, { key, label: labelOf(row), count: 1 });
      }
    });
    return Array.from(byKey.values());
  }

  outcomeOf(row: any): string {
    const status = String((row && row.status) || '').trim();
    return status || UNKNOWN_OUTCOME;
  }

  /** Local-day bounds, in epoch seconds, for the picked timeframe; null for all time. */
  private resolveDateRange(): { from: number; to: number } | null {
    const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime() / 1000;
    const endOfToday = startOfDay(new Date()) + 86400 - 1;
    const today = new Date();

    switch (this.selectedTimeframe) {
      case 'today':
        return { from: startOfDay(today), to: endOfToday };
      case 'week': {
        const start = new Date(today);
        start.setDate(start.getDate() - ((start.getDay() + 6) % 7));
        return { from: startOfDay(start), to: endOfToday };
      }
      case 'month':
        return { from: startOfDay(new Date(today.getFullYear(), today.getMonth(), 1)), to: endOfToday };
      case 'last30': {
        const start = new Date(today);
        start.setDate(start.getDate() - 29);
        return { from: startOfDay(start), to: endOfToday };
      }
      case 'custom': {
        // Half a custom range is not a range yet: hold at all time until both are set.
        if (!this.customStartDate || !this.customEndDate) {
          return null;
        }
        const from = this.parseIsoDate(this.customStartDate);
        const to = this.parseIsoDate(this.customEndDate);
        if (from == null || to == null) {
          return null;
        }
        return { from: Math.min(from, to), to: Math.max(from, to) + 86400 - 1 };
      }
      default:
        return null;
    }
  }

  private parseIsoDate(value: string): number | null {
    const parts = String(value).split('-').map(Number);
    if (parts.length !== 3 || parts.some(n => !Number.isFinite(n))) {
      return null;
    }
    return new Date(parts[0], parts[1] - 1, parts[2]).getTime() / 1000;
  }

  // ----- Pagination -----

  onPageSizeChange(size: any) {
    this.pageSize = Number(size) || 10;
    this.pageIndex = 1;
    this.updatePage();
  }

  goToPage(page: number | string) {
    if (typeof page !== 'number' || page < 1 || page > this.totalPages || page === this.pageIndex) {
      return;
    }
    this.pageIndex = page;
    this.updatePage();
  }

  isEllipsis(page: number | string): boolean {
    return page === '...';
  }

  trackByIndex(index: number): number {
    return index;
  }

  trackByLead(index: number, row: any): string {
    return (row && (row.job_id || row.client_number)) || String(index);
  }

  trackByKey(index: number, option: CountOption): string {
    return option.key;
  }

  private updatePage() {
    const total = this.filteredRows.length;
    this.totalPages = Math.max(1, Math.ceil(total / this.pageSize));
    this.pageIndex = Math.min(Math.max(1, this.pageIndex), this.totalPages);
    const start = (this.pageIndex - 1) * this.pageSize;
    this.pageRows = this.filteredRows.slice(start, start + this.pageSize);
    this.pageNumbers = total ? buildPageNumbers({ totalPages: this.totalPages, currentPageNum: this.pageIndex }) : [];
    this.cd.markForCheck();
  }

  // ----- Row actions -----

  viewClientProfile(row: any) {
    this.resolveProject(row).subscribe(project => {
      const projectId = (project && project.project_id) || row.project_id;
      this.dialog.open(ClientProfileComponent, {
        disableClose: true,
        width: '45%',
        height: '100vh',
        position: { right: '0', top: '0', bottom: '0' },
        panelClass: ['add-edit-role-dialog', 'client-profile-panel'],
        data: {
          projectDetails: project,
          projectId,
          callDetails: {
            people_id: row.people_id,
            project_id: row.project_id || projectId,
            client_number: row.client_number,
            client_name: row.client_name,
            client_email: row.client_email,
            whatsapp_chat_history: row.whatsapp_chat_history
          },
          selectedTabIndex: 0,
          // An RM works the lead from its history; they don't start calls or
          // watch them live from here.
          hideLiveTranscription: this.isMine,
          hideConnect: this.isMine
        },
        autoFocus: true
      });
    }, () => this.notify.showMessage('Could not load the project for this lead', 5000));
  }

  openEditOutcomeDialog(row: any, event?: Event) {
    this.stop(event);
    this.resolveProject(row).subscribe(project => {
      const dialogRef = this.dialog.open(EditOutcomePopupComponent, {
        width: '400px',
        maxWidth: '95vw',
        panelClass: 'add-edit-staff-dialog',
        disableClose: true,
        autoFocus: true,
        data: {
          call: row,
          projectId: (project && project.project_id) || row.project_id,
          expectedOutcomes: (project && project.expected_outcomes) || []
        }
      });
      dialogRef.afterClosed().subscribe(saved => {
        if (saved) {
          this.refresh();
        }
      });
    }, () => this.notify.showMessage('Could not load the project for this lead', 5000));
  }

  seeCallSummary(row: any, event?: Event) {
    this.stop(event);
    this.resolveProject(row).subscribe(project => {
      const dialogRef = this.dialog.open(CallSummaryPopupComponent, {
        width: '800px',
        panelClass: 'add-edit-staff-dialog',
        disableClose: true,
        data: {
          status: row.status,
          status_reason: row.status_reason,
          call_transcript: row.call_transcript,
          client_name: row.client_name,
          questions_asked: row.questions_asked,
          call_sid: row.call_sid,
          recording_url: row.recording_url,
          job_id: row.job_id,
          project_id: row.project_id || (project && project.project_id),
          people_id: row.people_id,
          ai_outcome: row.ai_outcome,
          human_reviewed_by: row.human_reviewed_by,
          human_reviewed_at: row.human_reviewed_at,
          add_in_other_project_approved: row.add_in_other_project_approved,
          expectedOutcomes: (project && project.expected_outcomes) || [],
          signals: extractPostCallSignals(row),
          entityId: this.profile && this.profile.entity && this.profile.entity.id,
          photo_url: row.photo_url || '',
          client_number: row.client_number || '',
          task_prompt: resolveLeadTaskPrompt(row),
        }
      });
      dialogRef.afterClosed().subscribe(saved => {
        if (saved) {
          this.refresh();
        }
      });
    }, () => this.notify.showMessage('Could not load the project for this lead', 5000));
  }

  openEditTasksDialog(row: any, event?: Event) {
    this.stop(event);
    const dialogRef = this.dialog.open(EditTasksPopupComponent, {
      width: '600px',
      maxHeight: '90vh',
      panelClass: 'add-edit-staff-dialog',
      disableClose: true,
      data: {
        open_tasks: row.open_tasks,
        closed_tasks: row.closed_tasks,
        client_name: row.client_name,
        call_id: row.call_sid,
        people_id: row.people_id,
        job_id: row.job_id
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.refresh();
      }
    });
  }

  /**
   * Project details for a row action: the page's project when it matches,
   * otherwise fetched once and cached (My Leads spans projects).
   */
  private resolveProject(row: any): Observable<any> {
    const projectId = row && row.project_id;
    const current = this.projectDetails;
    if (current && (!projectId || current.project_id === projectId)) {
      return of(current);
    }
    const entityId = this.getEntityId();
    if (!projectId || !entityId) {
      return of(null);
    }
    if (this.projectCache.has(projectId)) {
      return of(this.projectCache.get(projectId));
    }
    return this.jobService.getHoaProject(entityId, projectId).pipe(
      tap((project: any) => {
        if (project && project.project_id) {
          this.projectCache.set(projectId, project);
        }
      }),
      catchError(err => {
        console.error('Failed to load project for lead:', err);
        throw err;
      })
    );
  }

  // ----- Open tasks -----

  getFirstOpenTask(row: any): any {
    const tasks = row && row.open_tasks && row.open_tasks.has_open_task && row.open_tasks.tasks;
    return tasks && tasks.length ? tasks[0] : null;
  }

  getOpenTaskCount(row: any): number {
    const tasks = row && row.open_tasks && row.open_tasks.has_open_task && row.open_tasks.tasks;
    return tasks ? tasks.length : 0;
  }

  getTaskDescription(task: any): string {
    return (task && (task.task_description || task.task_prompt)) || '';
  }

  // ----- Documents -----

  getLeadDocumentEntries(documents: any): { key: string; url: string }[] {
    if (!documents || typeof documents !== 'object') {
      return [];
    }
    return Object.keys(documents)
      .filter(key => typeof documents[key] === 'string' && documents[key].trim().length > 0)
      .map(key => ({ key, url: documents[key].trim() }));
  }

  formatDocumentTypeLabel(key: string): string {
    if (!key) {
      return 'Document';
    }
    return key.split(/[_\s]+/).filter(Boolean)
      .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(' ');
  }

  downloadLeadDocument(url: string, key: string, event: Event) {
    this.stop(event);
    if (!url) {
      return;
    }
    openPresignedDownload(url, this.formatDocumentTypeLabel(key));
  }

  /** The WhatsApp media zip is only offered on leads that have documents to zip. */
  canDownloadWhatsAppMedia(row: any): boolean {
    return !!(row && row.client_number && row.client_number !== 'Reveal'
      && this.getLeadDocumentEntries(row.documents).length);
  }

  downloadLeadWhatsAppDocuments(row: any, event?: Event) {
    this.stop(event);
    const projectId = row && row.project_id;
    const clientNumber = row && row.client_number;
    if (!projectId || !clientNumber || this.downloadingWaDocsClientNumber === clientNumber) {
      return;
    }
    this.downloadingWaDocsClientNumber = clientNumber;
    this.notify.showMessage('Preparing WhatsApp documents zip…', 3000);
    this.jobService.downloadProjectWhatsAppDocuments(projectId, clientNumber).subscribe({
      next: (response) => {
        this.downloadingWaDocsClientNumber = null;
        if (!response || !response.downloadUrl) {
          this.notify.showMessage('Download URL was not returned.', 4000);
          return;
        }
        openPresignedDownload(response.downloadUrl, response.zipFilename || `${clientNumber}_documents.zip`);
      },
      error: (err) => {
        this.downloadingWaDocsClientNumber = null;
        this.notify.showMessage(getWhatsAppDocumentsErrorMessage(err), 5000);
      }
    });
  }

  // ----- Recordings -----

  hasRecording(row: any): boolean {
    return !!(row && row.call_sid && row.recording_url && row.status !== 'Not Picked');
  }

  playRecording(row: any, event?: Event) {
    this.stop(event);
    const callSid = row.call_sid;
    const audioUrl = row.recording_url;
    if (this.currentAudio && this.currentRecordingUrl === audioUrl) {
      if (this.currentAudio.paused) {
        this.currentAudio.play();
      } else {
        this.currentAudio.pause();
      }
      this.playingRecordingId = callSid;
      this.cd.markForCheck();
      return;
    }
    this.resetAudio();
    this.currentAudio = new CspSafeAudioPlayer({
      onTimeUpdate: () => this.cd.markForCheck(),
      onDuration: () => this.cd.markForCheck(),
      onPlayingChange: () => this.cd.markForCheck(),
      onEnded: () => this.cd.markForCheck(),
      onError: (message) => {
        console.error('Recording playback failed:', audioUrl, message);
        this.cd.markForCheck();
      }
    });
    this.playingRecordingId = callSid;
    this.currentRecordingUrl = audioUrl;
    this.currentAudio.playUrl(audioUrl).catch((err) => {
      console.error('Recording playback failed:', audioUrl, getFetchAudioErrorMessage(err));
      this.cd.markForCheck();
    });
  }

  seekAudio(event: any) {
    if (this.currentAudio) {
      this.currentAudio.currentTime = parseFloat(event.target.value);
      this.cd.markForCheck();
    }
  }

  formatAudioTime(time: number | undefined): string {
    if (!time) {
      return '00:00';
    }
    const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
    return `${pad(Math.floor(time / 60))}:${pad(Math.floor(time % 60))}`;
  }

  private resetAudio() {
    if (this.currentAudio) {
      this.currentAudio.destroy();
    }
    this.currentAudio = null;
    this.playingRecordingId = null;
    this.currentRecordingUrl = null;
  }

  // ----- Export -----

  /** The filtered list, every page of it — exactly what the filters show. */
  exportFiltered() {
    if (!this.filteredRows.length) {
      this.notify.showMessage('No leads match these filters.', 3000);
      return;
    }
    const header = ['Name', 'Mobile', 'Project', 'Outcome', 'Outcome reason', 'Assigned to', 'Assigned for outcome',
      'Assigned on', 'Last activity', 'Duration (s)'];
    const lines = this.filteredRows.map(row => [
      row.client_name,
      row.client_number,
      row.project_name,
      this.outcomeOf(row),
      row.status_reason,
      row.assigned_user_name,
      row.assigned_outcome,
      this.formatDateTime(row.assigned_at),
      this.formatDateTime(row.start_time),
      row.duration
    ]);
    const escape = (v: any) => `"${`${v == null ? '' : v}`.replace(/"/g, '""')}"`;
    const csv = [header, ...lines].map(r => r.map(escape).join(',')).join('\n');
    // The BOM makes Excel read the file as UTF-8, so names outside ASCII survive.
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${this.isMine ? 'my_leads' : 'assigned_leads'}_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  // ----- Formatting -----

  formatDateTime(value: any): string {
    const seconds = this.toSeconds(value);
    if (!seconds) {
      return '';
    }
    const date = new Date(seconds * 1000);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const hours = date.getHours();
    const minutes = date.getMinutes();
    return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}, `
      + `${hours % 12 || 12}:${minutes < 10 ? '0' + minutes : minutes} ${hours >= 12 ? 'PM' : 'AM'}`;
  }

  private toSeconds(value: any): number {
    const n = typeof value === 'number' ? value : parseFloat(value);
    if (!Number.isFinite(n) || n <= 0) {
      return 0;
    }
    return n > 1e12 ? n / 1000 : n;
  }

  private projectIdOf(project: any): string | null {
    return (project && project.project_id) || null;
  }

  private getEntityId(): string | null {
    return (this.profile && this.profile.entity && this.profile.entity.id) || null;
  }

  private stop(event?: Event) {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
  }
}
